'use client';

import { AlertTriangle, CheckCircle, CircleAlert, Info } from 'lucide-react';
import React, { useState } from 'react';
import {
  Banner,
  BannerClose,
  BannerDescription,
  BannerIcon,
  BannerTitle,
} from '@core/components/ui/banner';

type NotificationBannerVariant =
  | 'default'
  | 'warning'
  | 'info'
  | 'destructive'
  | 'success';

interface NotificationBannerProps {
  title: string;
  description?: string;
  variant?: NotificationBannerVariant;
  icon?: 'alert' | 'info' | 'warning' | 'success';
  dismissible?: boolean;
  onDismiss?: () => void;
}

const iconMap = {
  alert: CircleAlert,
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle,
};

const NotificationBanner: React.FC<NotificationBannerProps> = ({
  title,
  description,
  variant = 'default',
  icon = 'info',
  dismissible = true,
  onDismiss,
}) => {
  const [isVisible, setIsVisible] = useState(true);

  if (!isVisible) return null;

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  const IconComponent = iconMap[icon];
  const isUrgent = variant === 'destructive' || variant === 'warning';

  return (
    <Banner
      variant={variant}
      role={isUrgent ? 'alert' : 'status'}
      aria-live={isUrgent ? 'assertive' : 'polite'}
      className='items-start rounded-lg shadow-lg'
    >
      <BannerIcon
        icon={IconComponent}
        className='mt-0.5'
      />
      <div className='flex flex-1 flex-col gap-0.5'>
        <BannerTitle>{title}</BannerTitle>
        {description && <BannerDescription>{description}</BannerDescription>}
      </div>
      {dismissible && <BannerClose onClose={handleDismiss} />}
    </Banner>
  );
};

export default NotificationBanner;
